import type { Move } from '../../../schemas/move';
import { sciamanoArchetypeMoves } from './archetypes';
import {
  sciamanoInitialMoves,
  sciamanoStartingAutoMoves,
  sciamanoStartingChoiceMoves,
} from './initial';
import { sciamanoAdvanced2to5Moves } from './advanced-2-5';
import { sciamanoAdvanced6to10Moves } from './advanced-6-10';

export {
  sciamanoArchetypeMoves,
  sciamanoInitialMoves,
  sciamanoStartingAutoMoves,
  sciamanoStartingChoiceMoves,
  sciamanoAdvanced2to5Moves,
  sciamanoAdvanced6to10Moves,
};

/**
 * Catalogo completo delle mosse dello Sciamano (manuale V1, sezione *Lo
 * Sciamano*, rr. 5443-5767).
 *
 * Aggregato di:
 *  - 5 archetipi (`class_special`)
 *  - 3 mosse iniziali automatiche + 3 mosse iniziali di scelta (`starting`,
 *    `pickCount: 1` sul gruppo di scelta — *Medium* / *Auspicio* / *Radici
 *    Profonde*)
 *  - 16 mosse avanzate L2-5 (`advanced_2_5`)
 *  - 14 mosse avanzate L6-10 (`advanced_6_10`)
 *
 * Totale: **41 mosse**.
 *
 * **Pattern stress-testati**:
 *
 *  - **Mistica diversa dal casting**: lo Sciamano è **non casteggiante**
 *    (`spellcasting: undefined`, come Bardo/Guerriero/Ladro/Monaco/Ranger).
 *    *Aiuto dall'Altro Mondo* è una mossa-tiro+CAR a prese (pattern
 *    *Meditazione* del Monaco e *Aura* del Paladino).
 *  - ***Che Tu Sia...*** (D1): **mossa-config** con `MoveSchema.choices` su 2
 *    opzioni (benevolenza/malevolenza) + `choiceCount: 1`. La scelta è al
 *    momento della presa (manuale r. 5573).
 *  - ***Bambole e Pendoli*** (D2): description-only, pattern coerente con
 *    *Allargare le Basi* (Chierico), *Mille Risorse* (Ladro), *Di Tutto un
 *    Po'* (Bardo), *Formazione Eterogenea* (Mago).
 *  - **FI-8 stub description-only** (D3): *Animare Zombi*, *Apocalisse Zombi*,
 *    *Collezionista di Teschi*, *Uno con lo Spirito* — pseudo-gregari.
 *    Refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi.
 *  - **Catene di prerequisiti L2-5 → L6-10** (3 catene): *Che Tu Sia...* →
 *    *Nel Bene o nel Male*; *Bambola Vudù* → *Magia Simpatetica*; *Onorevoli
 *    Padri* (auto) → *Concilio Ancestrale*.
 *  - **Mosse multiclasse** (FI-17): *Amico degli Spiriti* (L2-5) e *Figlio
 *    degli Spiriti* (L6-10) scelgono da Barbaro o Druido (extension al Mago
 *    via archetipo Elfo). Description-only.
 *  - **Forward-refs cross-class TARGET**: *Portavoce dell'Aldilà* è
 *    referenziata in `prerequisites.anyOf` da 2 mosse del Druido
 *    (`colui-che-parla-alle-cose`, `spiriti-guardiani`). Ora che lo Sciamano
 *    è trascritto l'ID esiste localmente — il `knownExternalIds` nei test del
 *    Druido è stato aggiornato (D4).
 *  - **Niente `MoveSchema.replaces`** strutturali (manuale coerente: nessuna
 *    avanzata sostituisce un'iniziale o una L2-5).
 */
export const sciamanoMoves: readonly Move[] = [
  ...sciamanoArchetypeMoves,
  ...sciamanoInitialMoves,
  ...sciamanoAdvanced2to5Moves,
  ...sciamanoAdvanced6to10Moves,
];
